import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import eventService from '../services/eventService';
import { setEventDetails } from '../redux/slices/bookingSlice';
import StepperButtons from '../components/StepperButtons';

const EventInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  let { selectedEvent, selectedCenter } = useSelector((state) => state.event);
  const { eventDetails } = useSelector((state) => state.booking);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const flyerSectionRef = useRef(null);
  const highlightsSectionRef = useRef(null);
  const [isFlyerModalOpen, setIsFlyerModalOpen] = useState(false);
  const [flyerModalUrl, setFlyerModalUrl] = useState('');
  const [flyerZoom, setFlyerZoom] = useState(1);

  // Fallback: Load from sessionStorage if Redux state is empty
  if (!selectedEvent) {
    try {
      const sessionData = sessionStorage.getItem('yk_event_booking');
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        selectedEvent = parsed.selectedEvent;
        selectedCenter = parsed.selectedCenter;
        console.log('EventInfo - Loaded from sessionStorage:', { selectedEvent, selectedCenter });
      }
    } catch (err) {
      console.error('Error loading from sessionStorage:', err);
    }
  }

  useEffect(() => {
    console.log('EventInfo - useEffect triggered');
    console.log('EventInfo - selectedEvent:', selectedEvent);
    
    if (!selectedEvent) {
      console.warn('EventInfo - No selectedEvent, redirecting to events');
      navigate('/events');
      return;
    }

    // Prevent duplicate calls
    if (hasFetchedRef.current) {
      console.log('EventInfo - Already fetched, skipping duplicate call');
      return;
    }

    const fetchEventDetails = async () => {
      try {
        hasFetchedRef.current = true;
        setLoading(true);
        console.log('EventInfo - Fetching details for event:', selectedEvent.event_Id);
        const data = await eventService.getCompleteEventDetails(selectedEvent.event_Id);
        console.log('EventInfo - Fetched event details:', data);
        console.log('EventInfo - Highlights:', data?.highlights);
        console.log('EventInfo - Coordinators:', data?.coordinators);
        dispatch(setEventDetails(data));

        const go = new URLSearchParams(location.search).get('go');
        if (go === 'service') {
          navigate('/service');
        }
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError('Failed to load event details');
        hasFetchedRef.current = false; // Reset on error to allow retry
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [selectedEvent, dispatch, navigate, location.search]);

  useEffect(() => {
    if (!isFlyerModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFlyerModalOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFlyerModalOpen]);

  // Scroll to highlights section when navigating from highlights View Details button
  useEffect(() => {
    if (location.state?.scrollToFlyer && !loading) {
      console.log('Attempting to scroll to highlights...');
      console.log('highlightsSectionRef.current:', highlightsSectionRef.current);
      
      // Wait for DOM to be fully rendered
      const scrollToHighlights = () => {
        if (highlightsSectionRef.current) {
          console.log('Scrolling to highlights section');
          highlightsSectionRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
        } else {
          console.log('Highlights section ref not found, retrying...');
          setTimeout(scrollToHighlights, 100);
        }
      };
      
      setTimeout(scrollToHighlights, 500);
    }
  }, [location.state, loading]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    const num = Number(amount);
    if (Number.isNaN(num)) return '';
    return `$${num.toFixed(2)}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatTimeRange = (from, to) => {
    if (!from || !to) return '';
    const fromDate = new Date(from);
    const toDate = new Date(to);
    return `${formatTime(fromDate.toTimeString().slice(0, 8))} - ${formatTime(toDate.toTimeString().slice(0, 8))}`;
  };

  // Debug logging (can be removed in production)
  // console.log('EventInfo - eventDetails:', eventDetails);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <i className="fas fa-exclamation-circle text-red-500 text-6xl mb-4"></i>
            <p className="text-red-600 text-lg">{error}</p>
            <button
              onClick={() => navigate('/events')}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-50/70 via-white to-rose-50/70 font-sans">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-purple-200/35 blur-3xl"></div>
        <div className="absolute top-10 -right-24 h-72 w-72 rounded-full bg-blue-200/35 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-rose-200/25 blur-3xl"></div>
      </div>
      
      <div className="relative container mx-auto px-4 py-8 pb-24">
        <div className="max-w-6xl mx-auto">
          {(() => {
            const event = eventDetails?.event || eventDetails?.Event || selectedEvent || null;
            const flyerUrl = (event?.event_Flyer_URL || event?.event_Image_URL || selectedEvent?.event_Image_URL || selectedEvent?.event_Image_URL)?.trim?.() || (event?.event_Flyer_URL || event?.event_Image_URL || selectedEvent?.event_Image_URL);

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-10">
                <div className="relative overflow-hidden rounded-[28px] bg-white/75 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-indigo-50/30 to-purple-50/30"></div>
                  <div className="relative p-6 md:p-8">
                    <div className="flex items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm border border-white/60">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm">
                          <img
                            src="https://stssevastorage.blob.core.windows.net/ykwebsite/logo.png"
                            alt="Yugal Kunj"
                            className="h-4 w-auto"
                          />
                        </span>
                        <span>Yugalkunj Events</span>
                      </div>

                      <div className="inline-flex items-center rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm border border-white/60">
                        Active
                      </div>
                    </div>

                    <h1 className="mt-6 font-display text-3xl md:text-5xl font-semibold tracking-tight text-gray-900 leading-[1.05]">
                      {event?.event_Name || selectedEvent?.event_Name}
                    </h1>

                    <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-gray-700">
                      {(event?.event_Date || selectedEvent?.event_Date) && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 shadow-sm border border-white/60">
                          <i className="fa-solid fa-calendar-days text-indigo-600"></i>
                          <span className="font-medium">{formatDate(event?.event_Date || selectedEvent?.event_Date)}</span>
                        </span>
                      )}

                      {eventDetails?.timeOptions?.length > 0 ? (
                        eventDetails.timeOptions
                          .filter(timeOption => timeOption.time_Option_Name?.toLowerCase() !== "not sure")
                          .map((timeOption, idx) => {
                          const formatTime = (dateTimeStr) => {
                            if (!dateTimeStr) return '';
                            const date = new Date(dateTimeStr);
                            return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                          };
                          const startTime = formatTime(timeOption.date_Time_From);
                          const endTime = formatTime(timeOption.date_Time_To);
                          
                          return (
                            <span key={idx} className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 shadow-sm border border-white/60">
                              <i className="fa-regular fa-clock text-purple-600"></i>
                              <span className="font-medium">
                                {timeOption.time_Option_Name}
                                {startTime && endTime && ` (${startTime} - ${endTime})`}
                              </span>
                            </span>
                          );
                        })
                      ) : (event?.event_Time || selectedEvent?.event_Time) && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 shadow-sm border border-white/60">
                          <i className="fa-regular fa-clock text-purple-600"></i>
                          <span className="font-medium">{event?.event_Time || selectedEvent?.event_Time}</span>
                        </span>
                      )}

                      {(event?.event_Location || selectedEvent?.event_Location) && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 shadow-sm border border-white/60">
                          <i className="fa-solid fa-location-dot text-rose-600"></i>
                          <span className="font-medium">{event?.event_Location || selectedEvent?.event_Location}</span>
                        </span>
                      )}
                    </div>

                    <p className="mt-5 text-sm md:text-base text-gray-600 leading-relaxed">
                      {eventDetails?.Event?.event_Description || 
                       eventDetails?.event?.event_Description || 
                       eventDetails?.event_Description || 
                       selectedEvent?.event_Description || 
                       'No description available'}
                    </p>

                    <div className="mt-7 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => navigate('/service')}
                        className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-shadow"
                      >
                        Register for Pooja 
                      </button>

                      {flyerUrl && (
                        <button
                          type="button"
                          onClick={() => {
                            setFlyerModalUrl(flyerUrl);
                            setFlyerZoom(1);
                            setIsFlyerModalOpen(true);
                          }}
                          className="inline-flex items-center justify-center rounded-full bg-white/90 hover:bg-white px-6 py-3 text-sm font-semibold text-gray-800 border border-gray-200 shadow-sm hover:shadow-md transition-all"
                        >
                          View Flyer
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div ref={flyerSectionRef} className="relative overflow-hidden rounded-[28px] bg-white/75 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-indigo-50/25 to-purple-50/25"></div>
                  <div className="relative p-5 md:p-6">
                    <div className="flex items-center justify-between gap-3">
                      <div className="inline-flex items-center rounded-full bg-white/85 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm border border-white/60">
                        Featured Flyer
                      </div>
                      <div className="inline-flex items-center rounded-full bg-white/85 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm border border-white/60">
                        {(event?.event_Name || selectedEvent?.event_Name || 'Event')?.split(' ')?.slice(0, 2)?.join(' ')}
                      </div>
                    </div>

                    {flyerUrl ? (
                      <div className="mt-4 relative overflow-hidden rounded-3xl">
                        <div className="pointer-events-none absolute inset-0">
                          <div className="absolute -left-10 top-12 h-28 w-28 rounded-full bg-indigo-500/25 blur-2xl"></div>
                          <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-purple-500/25 blur-2xl"></div>
                          <div className="absolute right-10 bottom-10 h-32 w-32 rounded-full bg-amber-400/25 blur-2xl"></div>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-amber-500">
                          <img
                            src={flyerUrl}
                            alt={event?.event_Name || selectedEvent?.event_Name}
                            className="w-full h-auto object-contain opacity-90"
                            onError={(e) => {
                              if (e.target.src === event?.event_Flyer_URL?.trim?.() && (event?.event_Image_URL || selectedEvent?.event_Image_URL)) {
                                e.target.src = (event?.event_Image_URL || selectedEvent?.event_Image_URL)?.trim?.() || (event?.event_Image_URL || selectedEvent?.event_Image_URL);
                              } else {
                                e.target.src = 'https://via.placeholder.com/800x600?text=Event';
                              }
                            }}
                          />
                        </div>

                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-black/5 to-transparent"></div>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-3xl bg-gradient-to-br from-indigo-600 via-purple-600 to-amber-500 p-10 text-white/90">
                        Flyer not available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 space-y-8">
              {/* Event Highlights */}
              {(() => {
                const highlights = eventDetails?.highlights || eventDetails?.Highlights || [];
                
                if (highlights.length > 0) {
                  return (
                    <div ref={highlightsSectionRef} className="relative overflow-visible rounded-[24px] bg-white/80 backdrop-blur-xl shadow-[0_18px_55px_-40px_rgba(15,23,42,0.55)] border border-white/60">
                      <div className="absolute inset-0 rounded-[24px] bg-gradient-to-br from-white/60 via-amber-50/30 to-rose-50/20"></div>
                      <div className="relative p-6 md:p-7">
                        <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <i className="fas fa-star text-amber-500"></i>
                          Event Highlights
                        </h2>
                        <div className="flex flex-wrap gap-2">
                          {highlights.map((highlight, index) => {
                            const name = highlight.event_Highlight_Name || highlight.Event_Highlight_Name || 'Untitled';
                            const description = highlight.event_Highlight_Description || highlight.Event_Highlight_Description || '';

                            return (
                              <div key={index} className="relative group">
                                <span
                                  tabIndex={description ? 0 : -1}
                                  className="inline-flex rounded-full bg-gradient-to-r from-indigo-500/70 via-purple-500/70 to-amber-400/70 p-[1px] shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:-translate-y-0.5"
                                >
                                  <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-3 py-1 text-xs font-semibold text-gray-900 border border-white/70 transition-colors duration-300 group-hover:bg-white">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600"></span>
                                    {name}
                                  </span>
                                </span>

                                {description && (
                                  <div className="pointer-events-none absolute left-0 bottom-full z-30 mb-3 hidden w-[18rem] max-w-[85vw] rounded-2xl border border-white/70 bg-white/95 px-4 py-3 text-xs leading-relaxed text-slate-700 shadow-[0_18px_55px_-25px_rgba(15,23,42,0.35)] backdrop-blur-xl ring-1 ring-black/5 group-hover:block group-focus-within:block">
                                    <div className="absolute -bottom-1 left-6 h-2 w-2 rotate-45 bg-white/95 ring-1 ring-black/5"></div>
                                    <div className="text-[11px] font-semibold text-indigo-700 mb-1">{name}</div>
                                    <div className="text-slate-700">{description}</div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Event Schedule */}
              {(() => {
                const timeOptions = eventDetails?.timeOptions || eventDetails?.TimeOptions || [];
                if (timeOptions.length > 0) {
                  return (
                    <div className="relative overflow-hidden rounded-[24px] bg-white/80 backdrop-blur-xl shadow-[0_18px_55px_-40px_rgba(15,23,42,0.55)] border border-white/60">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-indigo-50/30 to-purple-50/20"></div>
                      <div className="relative p-6 md:p-7">
                        <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <i className="fas fa-clock text-indigo-600"></i>
                          Event Schedule
                        </h2>

                        <div className="space-y-3">
                          {timeOptions
                            .filter(timeOption => timeOption.time_Option_Name?.toLowerCase() !== "not sure")
                            .map((timeOption) => (
                            <div
                              key={timeOption.event_Time_Option_Id}
                              className="flex items-start justify-between gap-4 rounded-2xl bg-white/90 border border-gray-200 px-4 py-3 shadow-sm"
                            >
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">
                                  {timeOption.time_Option_Name}
                                </p>
                                {timeOption.time_Option_Description && (
                                  <p className="mt-0.5 text-sm text-gray-600 line-clamp-2">
                                    {timeOption.time_Option_Description}
                                  </p>
                                )}
                              </div>
                              <div className="shrink-0">
                                <span className="inline-flex items-center rounded-full bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-700 border border-gray-200">
                                  {formatTimeRange(timeOption.date_Time_From, timeOption.date_Time_To)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Event Coordinators */}
              {(() => {
                const coordinators = eventDetails?.coordinators || eventDetails?.Coordinators || [];
                
                if (coordinators.length > 0) {
                  return (
                    <div className="relative overflow-hidden rounded-[24px] bg-white/80 backdrop-blur-xl shadow-[0_18px_55px_-40px_rgba(15,23,42,0.55)] border border-white/60">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-blue-50/25 to-purple-50/25"></div>
                      <div className="relative p-6 md:p-7">
                        <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <i className="fas fa-user-tie text-purple-600"></i>
                          Event Coordinators
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {coordinators.map((coordinator, index) => (
                            <div key={index} className="rounded-2xl bg-white/90 border border-gray-200 p-4 shadow-sm">
                              <div className="flex items-start gap-3">
                                <div className="w-11 h-11 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                                  {(coordinator.coOrdinator_Name || coordinator.coordinator_Name)?.charAt(0) || 'C'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 truncate">
                                    {coordinator.coOrdinator_Name || coordinator.coordinator_Name || 'Coordinator'}
                                  </p>
                                  {(coordinator.coOrdinator_Email || coordinator.coordinator_Email) && (
                                    <p className="text-sm text-gray-600 truncate">
                                      <i className="fas fa-envelope mr-1"></i>
                                      {coordinator.coOrdinator_Email || coordinator.coordinator_Email}
                                    </p>
                                  )}
                                  {(coordinator.coOrdinator_Phone_Number || coordinator.coordinator_Phone) && (
                                    <p className="text-sm text-gray-600 truncate">
                                      <i className="fas fa-phone mr-1"></i>
                                      {coordinator.coOrdinator_Phone_Number || coordinator.coordinator_Phone}
                                    </p>
                                  )}
                                  {coordinator.coOrdinator_Description && (
                                    <p className="text-xs text-gray-500 mt-1 italic line-clamp-2">
                                      {coordinator.coOrdinator_Description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div className="lg:col-span-5 space-y-8">
              {/* Pooja/Seva options */}
              {(() => {
                const services = eventDetails?.services || eventDetails?.Services || [];
                if (services.length > 0) {
                  return (
                    <div className="relative overflow-hidden rounded-[24px] bg-white/80 backdrop-blur-xl shadow-[0_18px_55px_-40px_rgba(15,23,42,0.55)] border border-white/60">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-indigo-50/20 to-amber-50/25"></div>
                      <div className="relative p-6 md:p-7">
                        <h2 className="text-base font-semibold text-gray-900 mb-4">Pooja/Seva Options</h2>
                        <div className="space-y-3">
                          {services.map((service) => (
                            <div key={service.event_Service_Id} className="flex items-start justify-between gap-4 rounded-2xl bg-white/90 border border-gray-200 px-4 py-3 shadow-sm">
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">{service.service_Name}</p>
                                {service.service_Description && (
                                  <p className="mt-0.5 text-sm text-gray-600 line-clamp-2">{service.service_Description}</p>
                                )}
                              </div>
                              <div className="shrink-0 text-right">
                                <span className="text-sm font-semibold text-gray-900">
                                  {service.is_Free ? '$0.00' : formatCurrency(service.service_Price)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Add-ons */}
              {(() => {
                const optionalServices = eventDetails?.optionalServices || eventDetails?.OptionalServices || [];
                if (optionalServices.length > 0) {
                  return (
                    <div className="relative overflow-hidden rounded-[24px] bg-white/80 backdrop-blur-xl shadow-[0_18px_55px_-40px_rgba(15,23,42,0.55)] border border-white/60">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-purple-50/25 to-rose-50/20"></div>
                      <div className="relative p-6 md:p-7">
                        <h2 className="text-base font-semibold text-gray-900 mb-4">Add-ons</h2>
                        <div className="space-y-3">
                          {optionalServices.map((service) => (
                            <div key={service.optional_Service_Id} className="flex items-start justify-between gap-4 rounded-2xl bg-white/90 border border-gray-200 px-4 py-3 shadow-sm">
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">{service.optional_Service_Name}</p>
                                {service.optional_Service_Description && (
                                  <p className="mt-0.5 text-sm text-gray-600 line-clamp-2">{service.optional_Service_Description}</p>
                                )}
                              </div>
                              <div className="shrink-0 text-right">
                                <span className="text-sm font-semibold text-gray-900">
                                  {service.is_Free ? '$0.00' : formatCurrency(service.optional_Service_Price)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>

        </div>
      </div>

      <StepperButtons
        backPath="/events"
        nextPath="/service"
        backLabel="Back to Events"
        nextLabel="Register for Pooja"
      />

      {isFlyerModalOpen && flyerModalUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-3 sm:p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setIsFlyerModalOpen(false);
            }
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-5xl max-h-[88vh] overflow-hidden rounded-2xl bg-white shadow-2xl border border-white/50">
            <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gradient-to-br from-white via-slate-50 to-indigo-50">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {eventDetails?.event?.event_Name || eventDetails?.Event?.event_Name || selectedEvent?.event_Name || 'Flyer'}
                </div>
                <div className="text-[11px] text-gray-500">Flyer Preview</div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-gray-700 border border-gray-200 shadow-sm">
                  {Math.round(flyerZoom * 100)}%
                </span>

                <button
                  type="button"
                  onClick={() => setFlyerZoom((z) => Math.max(0.5, Number((z - 0.1).toFixed(2))))}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm"
                  aria-label="Zoom out"
                >
                  <i className="fas fa-minus text-xs"></i>
                </button>

                <button
                  type="button"
                  onClick={() => setFlyerZoom((z) => Math.min(3, Number((z + 0.1).toFixed(2))))}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm"
                  aria-label="Zoom in"
                >
                  <i className="fas fa-plus text-xs"></i>
                </button>

                <button
                  type="button"
                  onClick={() => setFlyerZoom(1)}
                  className="inline-flex items-center justify-center rounded-full bg-white hover:bg-gray-50 px-3 h-9 text-[11px] font-semibold text-gray-700 border border-gray-200 shadow-sm"
                >
                  Reset
                </button>

                <button
                  type="button"
                  onClick={() => setIsFlyerModalOpen(false)}
                  className="inline-flex items-center justify-center rounded-full bg-white hover:bg-gray-50 px-3 h-9 text-[11px] font-semibold text-gray-700 border border-gray-200 shadow-sm"
                  aria-label="Close flyer"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-amber-500">
              <div className="max-h-[calc(88vh-56px)] overflow-auto bg-black/5 p-2 sm:p-3">
                <div className="w-full flex items-center justify-center">
                  <img
                    src={flyerModalUrl}
                    alt="Event flyer"
                    className="rounded-xl bg-white shadow-md"
                    style={{
                      transform: `scale(${flyerZoom})`,
                      transformOrigin: 'center top',
                      maxWidth: '100%',
                      height: 'auto',
                    }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x600?text=Flyer';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventInfo;
